// Role-aware step definitions for the onboarding tour.
//
// Each step is a driver.js DriveStep optionally tagged with a `route`.
// The Onboarding runner navigates to that route (if not already there)
// and waits for the step's anchor to appear before showing the popover —
// so the tour walks the user across real pages (home → discover →
// contracts) and spotlights real elements there.
//
// Step kinds:
//   - Anchored (`element` + optional `route`): navigates if needed, then
//     spotlights the element. If the anchor never appears, the runner
//     falls back to showing the popover centered (never stalls).
//   - Narrated (no `element`): a centered card, for deep-flow stages
//     (交付/收款/验收/官方账号) that live inside a specific contract or
//     chat we can't reliably anchor.
//
// Anchors:
//   Header (every page):  account · publish · find-creators · browse-briefs · messages
//   Home page:            home-showcase (作品 / 创作者 精选区)
//   Discover page:        discover-tabs · discover-search · discover-filter
//   Contracts list:       contracts-role-toggle

import type { DriveStep } from 'driver.js';
import { OFFICIAL_STUDIO_PROFILE_PATH } from '../../constants/official';

// A step can carry a route to visit before it runs.
export type TourStep = DriveStep & { route?: string };

const card = (title: string, description: string): TourStep => ({
  popover: { title, description, align: 'center' },
});

function officialCard(title: string, description: string): TourStep {
  return {
    route: OFFICIAL_STUDIO_PROFILE_PATH,
    popover: {
      title,
      description: `${description}\n\n已为你打开云作studio主页，点击「开始合作」即可进入沟通。`,
      align: 'center',
    },
  };
}

// Step that points at the home page 作品 / 创作者 showcase. Shared by
// both roles — discovering existing work and creators early is the most
// important first impression of the platform.
const homeShowcaseStep: TourStep = {
  route: '/',
  element: '[data-tour="home-showcase"]',
  popover: {
    title: '逛逛首页',
    description: '先浏览首页的精选作品与创作者，感受平台的能力与风格，找找灵感与合作方向。',
    side: 'top',
    align: 'center',
  },
};

// Creator (乙方) journey — showcase work, find briefs, deliver, get paid.
export const creatorTour: TourStep[] = [
  {
    route: '/',
    element: '[data-tour="account"]',
    popover: {
      title: '设置账号',
      description: '点开右上角头像，进入「个人主页 / 通用设置」，完善昵称与简介，让客户更快了解你。',
      side: 'bottom', align: 'end',
    },
  },
  {
    route: '/',
    element: '[data-tour="publish"]',
    popover: {
      title: '发布作品',
      description: '点击「发布」上传作品，展示你的能力与风格，吸引客户主动联系。',
      side: 'bottom', align: 'end',
    },
  },
  homeShowcaseStep,
  {
    route: '/',
    element: '[data-tour="publish"]',
    popover: {
      title: '发布服务',
      description: '同样在「发布」里创建服务（套餐 + 报价），让客户可以直接下单。',
      side: 'bottom', align: 'end',
    },
  },
  {
    route: '/discover?tab=briefs',
    element: '[data-tour="discover-tabs"]',
    popover: {
      title: '浏览需求',
      description: '切换到「浏览需求」，查看客户发布的项目，找到适合你的机会。',
      side: 'bottom', align: 'start',
    },
  },
  {
    route: '/discover?tab=briefs',
    element: '[data-tour="discover-filter"]',
    popover: {
      title: '筛选需求',
      description: '用预算、品类、交付周期等条件筛选需求，快速锁定最匹配的项目，再向客户报价。',
      side: 'bottom', align: 'end',
    },
  },
  {
    route: '/discover?tab=briefs',
    element: '[data-tour="messages"]',
    popover: {
      title: '聊一聊',
      description: '通过站内「消息」和客户沟通需求细节，全程留痕，更有保障。',
      side: 'bottom', align: 'end',
    },
  },
  {
    route: '/contracts',
    element: '[data-tour="contracts-role-toggle"]',
    popover: {
      title: '发起合同',
      description: '沟通一致后可发起合同。在「项目管理」用「我是创作者」查看你接单的合同，约定金额、交付内容与验收标准。',
      side: 'bottom', align: 'start',
    },
  },
  card('设置里程碑', '把项目拆成几个里程碑，每个里程碑对应一笔款项，分阶段交付、分阶段收款，风险更低。'),
  card('交付作品', '在合同里提交作品（最多 5 个文件）。客户可在线预览、下载，并在验收期内确认。'),
  card('收款', '客户验收通过后，平台托管的款项会放款到你的账户；尾款在最终验收后结清。'),
  officialCard('关注官方账号', '关注官方账号，第一时间获取平台活动、约稿机会与创作者扶持政策。'),
];

// Client (甲方) journey — post briefs, find creators, pay, accept.
export const clientTour: TourStep[] = [
  {
    route: '/',
    element: '[data-tour="account"]',
    popover: {
      title: '设置账号',
      description: '点开右上角头像，进入「个人主页 / 通用设置」，完善昵称与简介，方便创作者了解你。',
      side: 'bottom', align: 'end',
    },
  },
  {
    route: '/',
    element: '[data-tour="publish"]',
    popover: {
      title: '发布需求',
      description: '点击「发布」选择发布需求，描述项目与预算，让创作者主动来接。',
      side: 'bottom', align: 'end',
    },
  },
  homeShowcaseStep,
  {
    route: '/discover?tab=creators',
    element: '[data-tour="discover-tabs"]',
    popover: {
      title: '浏览作品',
      description: '在「找创作者」里浏览作品与服务，直观感受每位创作者的能力。',
      side: 'bottom', align: 'start',
    },
  },
  {
    route: '/discover?tab=creators',
    element: '[data-tour="discover-filter"]',
    popover: {
      title: '筛选创作者',
      description: '用品类、价位、风格等条件筛选创作者，挑选最合适的人选再发起沟通。',
      side: 'bottom', align: 'end',
    },
  },
  {
    route: '/discover?tab=creators',
    element: '[data-tour="messages"]',
    popover: {
      title: '聊一聊',
      description: '通过站内「消息」和创作者沟通需求，确认方案后再下单，全程留痕。',
      side: 'bottom', align: 'end',
    },
  },
  {
    route: '/contracts',
    element: '[data-tour="contracts-role-toggle"]',
    popover: {
      title: '发起合同',
      description: '沟通一致后可发起合同。在「项目管理」用「我是需求方」查看你发起的合同，明确金额、交付内容与验收标准。',
      side: 'bottom', align: 'start',
    },
  },
  card('设置里程碑', '把项目拆成几个里程碑分阶段进行：每完成并验收一个里程碑，再进入下一阶段，更可控。'),
  card('打款和放款', '按里程碑把款项打给平台托管；验收通过后再由平台放款给创作者，资金更安全。'),
  card('验收', '创作者交付后，在验收期内确认验收、要求修改或发起申诉；超时未操作将自动验收。'),
  officialCard('先和官方工作室聊聊', '不确定怎么开始？可以先和官方工作室聊聊需求，我们帮你梳理方案、匹配合适的创作者。'),
];

export function tourForRole(role?: string): TourStep[] {
  return role === 'client' ? clientTour : creatorTour;
}
